/**
 * The read-only look of a learned pattern, shared by the Learning editor's preview screen and the Learning Catalog's detail
 * popups: a stack of labelled sections (le-* classes from styles/learning-editor.css). Presentational only: every label and
 * value comes from the caller. The hanging row numbers of `.le-plain` lists need the `.le-fields` stack around them.
 */
import React from "react";

import "./styles/learning-editor.css";

/** One labelled section of the stack. */
export function Section({ label, className = "", children }) {
  return (
    <div className={`le-field ${className}`.trim()}>
      <span className="le-label">{label}</span>
      {children}
    </div>
  );
}

/** A one-line section: the label and its value side by side (the category). */
export function InlineSection({ label, value }) {
  return (
    <div className="le-field le-field--inline">
      <span className="le-label">{label}</span>
      <span className="le-inline-value">{value}</span>
    </div>
  );
}

/** A numbered list of text rows, or `empty` (muted) when there are none. */
export function NumberedText({ items, empty }) {
  if (!items || !items.length) return <p className="le-muted">{empty}</p>;
  return <ol className="le-plain">{items.map((text, i) => <li key={i}>{text}</li>)}</ol>;
}

/** Free text that keeps its line breaks, or `empty` (muted) when blank. */
export function BodyText({ text, empty }) {
  return text ? <p className="le-text">{text}</p> : <p className="le-muted">{empty}</p>;
}

/** The fix block: its kind in bold above the text, or `empty` (muted) when nothing was recorded. */
export function FixBlock({ kindLabel, text, empty }) {
  if (!kindLabel && !text) return <p className="le-muted">{empty}</p>;
  return (
    <div className="le-control">
      {kindLabel && <p className="le-cause-name">{kindLabel}</p>}
      {text && <p className="le-text">{text}</p>}
    </div>
  );
}

/** The screen's title block: a heading, an optional meta line and optional tags ({label, tone?, hint?}; the hint is the tooltip). */
export function ScreenIntro({ id, heading, meta, tags }) {
  const shown = (tags || []).filter(Boolean);
  return (
    <div className="le-intro">
      <h3 className="le-h" id={id}>{heading}</h3>
      {meta && <p className="le-muted">{meta}</p>}
      {shown.length > 0 && (
        <ul className="le-tags">
          {shown.map((tag) => (
            <li key={tag.label} className={`le-tag ${tag.tone ? `le-tag--${tag.tone}` : ""}`.trim()} title={tag.hint}>{tag.label}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The pattern's sections in the preview's order: category (when given, inline), symptoms, troubleshooting steps, root cause
 * and the fix. `labels` = {category, symptoms, steps, rootCause, fix}; `empty` is shown for a section with nothing in it.
 * `children` are further sections appended to the same stack.
 */
export default function PatternSections({ labels, category, symptoms, steps, rootCause, fixKindLabel, fixText, empty, children }) {
  return (
    <div className="le-fields">
      {category && <InlineSection label={labels.category} value={category} />}
      <Section label={labels.symptoms}><NumberedText items={symptoms} empty={empty} /></Section>
      <Section label={labels.steps}><NumberedText items={steps} empty={empty} /></Section>
      <Section label={labels.rootCause}><BodyText text={rootCause} empty={empty} /></Section>
      <Section label={labels.fix}><FixBlock kindLabel={fixKindLabel} text={fixText} empty={empty} /></Section>
      {children}
    </div>
  );
}
